import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { reportsApi } from '../../services/reportsApi';
import type { ReliabilityBundlePreviewResponse } from '../../services/reportsApi';

type SupportingPackStatus = 'idle' | 'generating' | 'ready' | 'error';

type UseSupportingPacksArgs = {
  canGenerateSupportingPacks: boolean;
  scopedOrgId?: string | null;
  supportingPackSiteId: string;
  supportingPackWindowStart: string;
  supportingPackWindowEnd: string;
  supportingPackScope: 'site' | 'device' | 'zone';
  supportingPackScopeId: string;
  reliabilityPreviewData?: ReliabilityBundlePreviewResponse | null;
  downloadBlob: (blob: Blob, filename: string) => void;
  describeTrustPackStartError: (response: any) => string;
};

export function useSupportingPacks({
  canGenerateSupportingPacks,
  scopedOrgId,
  supportingPackSiteId,
  supportingPackWindowStart,
  supportingPackWindowEnd,
  supportingPackScope,
  supportingPackScopeId,
  reliabilityPreviewData,
  downloadBlob,
  describeTrustPackStartError,
}: UseSupportingPacksArgs) {
  const normalizedOrgId = scopedOrgId || undefined;
  const [reliabilityPackStatus, setReliabilityPackStatus] = useState<SupportingPackStatus>('idle');
  const [reliabilityPackError, setReliabilityPackError] = useState('');
  const [reliabilityPackPreview, setReliabilityPackPreview] = useState<ReliabilityBundlePreviewResponse | null>(null);
  const [customerHandoffStatus, setCustomerHandoffStatus] = useState<SupportingPackStatus>('idle');
  const [customerHandoffError, setCustomerHandoffError] = useState('');
  const [customerHandoffJobId, setCustomerHandoffJobId] = useState<string | null>(null);
  const [customerHandoffReadyJobId, setCustomerHandoffReadyJobId] = useState<string | null>(null);

  useEffect(() => {
    if (reliabilityPreviewData?.ok) {
      setReliabilityPackPreview(reliabilityPreviewData);
      setReliabilityPackStatus('ready');
      setReliabilityPackError('');
    }
  }, [reliabilityPreviewData]);

  const runReliabilityPackGeneration = useCallback(async (opts?: { download?: boolean }) => {
    if (!canGenerateSupportingPacks) return;
    setReliabilityPackStatus('generating');
    setReliabilityPackError('');
    try {
      const preview = await reportsApi.getReliabilityBundlePreview({
        start_ts: supportingPackWindowStart,
        end_ts: supportingPackWindowEnd,
        org_id: normalizedOrgId,
        site_id: supportingPackSiteId,
        scope: supportingPackScope,
        scope_id: supportingPackScopeId || undefined,
      });
      if (!preview?.ok) {
        throw new Error('Reliability package could not be generated.');
      }
      setReliabilityPackPreview(preview);
      setReliabilityPackStatus('ready');
      if (opts?.download) {
        const { blob, filename } = await reportsApi.downloadReliabilityBundle({
          start_ts: supportingPackWindowStart,
          end_ts: supportingPackWindowEnd,
          org_id: normalizedOrgId,
          site_id: supportingPackSiteId,
          scope: supportingPackScope,
          scope_id: supportingPackScopeId || undefined,
          format: 'pdf',
        });
        downloadBlob(blob, filename);
      }
    } catch (error: any) {
      setReliabilityPackStatus('error');
      setReliabilityPackError(String(error?.message || 'Reliability package could not be generated.'));
    }
  }, [
    canGenerateSupportingPacks,
    downloadBlob,
    normalizedOrgId,
    supportingPackSiteId,
    supportingPackScope,
    supportingPackScopeId,
    supportingPackWindowEnd,
    supportingPackWindowStart,
  ]);

  const runCustomerHandoffGeneration = useCallback(async () => {
    if (!canGenerateSupportingPacks) return;
    setCustomerHandoffStatus('generating');
    setCustomerHandoffError('');
    setCustomerHandoffReadyJobId(null);
    try {
      const started = await reportsApi.startTrustPackBuild({
        from_date: supportingPackWindowStart,
        to_date: supportingPackWindowEnd,
        org_id: normalizedOrgId,
        site_id: supportingPackSiteId,
      });
      if (!started?.ok || !started?.job_id) {
        throw new Error(describeTrustPackStartError(started));
      }
      setCustomerHandoffJobId(String(started.job_id).trim());
    } catch (error: any) {
      setCustomerHandoffStatus('error');
      setCustomerHandoffError(String(error?.message || 'Platform trust pack could not be started.'));
    }
  }, [
    canGenerateSupportingPacks,
    describeTrustPackStartError,
    normalizedOrgId,
    supportingPackSiteId,
    supportingPackWindowEnd,
    supportingPackWindowStart,
  ]);

  const downloadCustomerHandoffBundle = useCallback(async () => {
    if (!customerHandoffReadyJobId) {
      await runCustomerHandoffGeneration();
      return;
    }
    try {
      const { blob, filename } = await reportsApi.downloadTrustPackJob(customerHandoffReadyJobId);
      downloadBlob(blob, filename);
    } catch (error: any) {
      setCustomerHandoffStatus('error');
      setCustomerHandoffError(String(error?.message || 'Platform trust pack could not be downloaded.'));
    }
  }, [customerHandoffReadyJobId, downloadBlob, runCustomerHandoffGeneration]);

  useEffect(() => {
    if (!customerHandoffJobId) return;
    let cancelled = false;
    let timer: number | undefined;

    const poll = async () => {
      try {
        const status = await reportsApi.getTrustPackJobStatus(customerHandoffJobId);
        if (cancelled) return;
        const state = String(status?.status || '').trim().toLowerCase();
        if (state === 'completed' && status?.ok) {
          setCustomerHandoffStatus('ready');
          setCustomerHandoffReadyJobId(customerHandoffJobId);
          setCustomerHandoffJobId(null);
          return;
        }
        if (state === 'error' || state === 'cancelled') {
          setCustomerHandoffStatus('error');
          setCustomerHandoffError(String(status?.detail || status?.error || 'Platform trust pack failed.'));
          setCustomerHandoffJobId(null);
          return;
        }
        timer = window.setTimeout(() => {
          void poll();
        }, 2000);
      } catch (error: any) {
        if (cancelled) return;
        setCustomerHandoffStatus('error');
        setCustomerHandoffError(String(error?.message || 'Platform trust pack status could not be checked.'));
        setCustomerHandoffJobId(null);
      }
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [customerHandoffJobId]);

  const supportingPackOrchestrationKey = useMemo(
    () => canGenerateSupportingPacks
      ? [
          scopedOrgId,
          supportingPackSiteId,
          supportingPackWindowStart,
          supportingPackWindowEnd,
          supportingPackScope,
          supportingPackScopeId,
        ].join('::')
      : '',
    [
      canGenerateSupportingPacks,
      scopedOrgId,
      supportingPackSiteId,
      supportingPackScope,
      supportingPackScopeId,
      supportingPackWindowEnd,
      supportingPackWindowStart,
    ],
  );

  const supportingPackOrchestrationRef = useRef<string>('');

  useEffect(() => {
    if (!supportingPackOrchestrationKey) return;
    if (supportingPackOrchestrationRef.current === supportingPackOrchestrationKey) return;
    supportingPackOrchestrationRef.current = supportingPackOrchestrationKey;
    void runReliabilityPackGeneration();
    void runCustomerHandoffGeneration();
  }, [runCustomerHandoffGeneration, runReliabilityPackGeneration, supportingPackOrchestrationKey]);

  return {
    reliabilityPackStatus,
    reliabilityPackError,
    reliabilityPackPreview,
    customerHandoffStatus,
    customerHandoffError,
    customerHandoffReadyJobId,
    runReliabilityPackGeneration,
    runCustomerHandoffGeneration,
    downloadCustomerHandoffBundle,
  };
}
